import { Typography } from "antd";
import React, { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useDisclosure } from "../../../../../../hooks/useDisclosure";
import { AppDispatch } from "../../../../../../redux/store";
import { PopupConfirm } from "../../../../../atoms/Popup";
import { getTenantDetails, patchTenantDetails } from "../../ducks/actions";
import { mapVehicleToForm } from "./mapVehicleToForm";
import { Vehicle } from "./types";
import { VehicleDetailForm } from "./VehicleDetailForm";

type FormType = "add" | "edit" | "delete";

type UseVehicleDetailModalOptions = {
  userId: string;
  vehicles: Vehicle[];
};

export function useVehicleDetailModal(props: UseVehicleDetailModalOptions) {
  const { userId, vehicles } = props;

  const dispatch = useDispatch<AppDispatch>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isDeleting, setIsDeleting] = useState(false);
  const [formType, setFormType] = useState<FormType>("add");
  const [selectedRow, setSelectedRow] = useState<Vehicle | null>(null);

  const closeModal = useCallback(() => {
    onClose();
    setSelectedRow(null);
  }, []);

  const deleteVehicle = async (vehicleId: string) => {
    try {
      setIsDeleting(true);
      const payload = (vehicles ?? []).filter((vehicle) => vehicle?.vehicleId !== vehicleId);
      await patchTenantDetails(userId, { vehicleInfo: payload });
      await dispatch(getTenantDetails(userId));
    } catch (error) {
      console.error("Failed to delete vehicle:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpen = useCallback(
    (type: FormType, row?: Vehicle) => {
      if (type === "add" || type === "edit") {
        setFormType(type);
        setSelectedRow(row ?? null);
        onOpen();
      }

      if (type === "delete")
        PopupConfirm({
          title: "Are you sure you want to delete?",
          onOk: async () => await deleteVehicle(row?.vehicleId),
          okText: "Yes",
          okButtonProps: { loading: isDeleting },
        });
    },
    [deleteVehicle, isDeleting]
  );

  const popupContent = useMemo(
    () => ({
      add: {
        title: "Add Vehicle Details",
        content: <VehicleDetailForm key="add" onClose={closeModal} userId={userId} vehicles={vehicles} />,
      },
      edit: {
        title: "Edit Vehicle Details",
        content: (
          <VehicleDetailForm
            key={selectedRow?.vehicleId}
            userId={userId}
            vehicles={vehicles}
            onClose={closeModal}
            defaultValues={mapVehicleToForm(selectedRow)}
          />
        ),
      },
      delete: {
        title: "Delete Vehicle",
        content: (
          <Typography.Text>
            Are you sure you want to delete vehicle with plate <strong>{selectedRow?.plateNumber}</strong>?
          </Typography.Text>
        ),
      },
    }),
    [userId, vehicles, selectedRow, closeModal]
  );

  return {
    isOpen,
    formType,
    popupContent,
    handleOpen,
    closeModal,
  };
}
